/**
 * 实验场景分区。
 * 对应技术设计 §4.2（实验桌、器材托盘、操作区、废液区、安全区布局）
 * 与 §11.3 实验包里的 `scene.zones: ["tray", "workbench", "water_area", "waste_area"]`。
 *
 * 分区只描述**空间**，不描述任何具体实验（§4.2 `:240`「Scene 不知道『氧气实验』具体规则」）。
 */

import type { Rect } from "../eom/port.js";

export type ZoneKind = "tray" | "workbench" | "water" | "waste" | "safety";

export interface Zone {
  id: string;
  name: string;
  kind: ZoneKind;
  bounds: Rect;
}

export interface StageSize {
  width: number;
  height: number;
}

export const LAB_TABLE_DEFAULT_STAGE: StageSize = { width: 960, height: 780 };

/**
 * `lab_table_default` 版式：左侧固定器材托盘，中央实验主区足够大（§4.2 布局原则第 1 条）。
 * water_area 在 workbench 内部，是它的子区域，不是并列的第三块桌面。
 */
export const LAB_TABLE_DEFAULT_ZONES: readonly Zone[] = [
  { id: "tray", name: "器材托盘", kind: "tray", bounds: { x: 16, y: 16, width: 180, height: 748 } },
  { id: "workbench", name: "实验桌", kind: "workbench", bounds: { x: 212, y: 16, width: 732, height: 660 } },
  { id: "water_area", name: "水槽区", kind: "water", bounds: { x: 600, y: 470, width: 330, height: 190 } },
  { id: "waste_area", name: "废液区", kind: "waste", bounds: { x: 212, y: 690, width: 200, height: 74 } },
] as const;

const LAYOUTS: ReadonlyMap<string, { stage: StageSize; zones: readonly Zone[] }> = new Map([
  ["lab_table_default", { stage: LAB_TABLE_DEFAULT_STAGE, zones: LAB_TABLE_DEFAULT_ZONES }],
]);

export function listLayouts(): string[] {
  return [...LAYOUTS.keys()];
}

export function getLayout(layout: string): { stage: StageSize; zones: readonly Zone[] } {
  const found = LAYOUTS.get(layout);
  if (!found) {
    throw new Error(`未知场景版式 "${layout}"，可用版式：${listLayouts().join(", ")}`);
  }
  return found;
}

/** 按实验包声明的 zones 过滤出实际启用的分区，顺序按版式定义（渲染层叠序稳定）。 */
export function selectZones(layout: string, enabledZoneIds: string[]): Zone[] {
  const { zones } = getLayout(layout);
  const enabled = new Set(enabledZoneIds);
  const unknown = enabledZoneIds.filter((id) => !zones.some((zone) => zone.id === id));
  if (unknown.length > 0) {
    throw new Error(`版式 "${layout}" 不含分区：${unknown.join(", ")}。可用分区：${zones.map((z) => z.id).join(", ")}`);
  }
  return zones.filter((zone) => enabled.has(zone.id));
}

export function findZone(zones: Zone[], zoneId: string): Zone | undefined {
  return zones.find((zone) => zone.id === zoneId);
}
